import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { Observable, ReplaySubject, Subscription } from "rxjs";
import { Injectable } from "@angular/core";
import {
  SystemConfigPromiseService,
  SysFeatures,
  Roles,
  ActiveUserProfilePromiseService,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  PromiseSet,
} from "shared/cubiq-services";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import {
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "app/golden-layout/golden-layout-service";
import { LoggerService } from "@bfx/logger";
import { PlRequestOrderTypeStrategy, RequestOrderType } from "shared/cubiq-model-multi-asset";
const logger = LoggerService.for(__dirname);
//const SAVED_USER_SETTINGS = ProfileSettingKeys.userOrderEntrySettings;

@Injectable()
export class OESettingsService {
  /**********************************************************************/
  /* DATA */
  private primedSubject = new ReplaySubject<void>(1);
  public primed$: Observable<void> = this.primedSubject.asObservable();
  protected typesPromise: PromiseSet<void> = new PromiseSet<void>();

  public independentComments: boolean = true;
  public privateCommentsEnabled: boolean = true;
  public privatizeClientComments: boolean = true;
  public privatizePrivateComments: boolean = true;
  // From KD:
  //	- For MODIFY the setting applies for Modify In and Entry.
  //	- N = Prompt to abort or leave open, Y= No Prompt and order is left in acceptance
  // Therefore:
  // 	if(this.control.showDialogOnAbort === true)  =>  Prompt to abort, then act according to user selection
  // 	if(this.control.showDialogOnAbort === false)  =>  No Prompt and order is left in acceptance, just close the window
  public enableDebugBtn: boolean = false;
  ///
  public currentUserId: string = null;
  ////
  public multiplyAmountUserValue: number = 0;
  public multiplyAmountUserToggle: boolean = false;
  ///
  public strictClientDataset: boolean = false;
  ///
  public maximumGammaRungs: number = 20;
  ///
  public newOrderTypeDefault: string = null;

  protected profileUpdatesSub: Subscription;

  private config: {
    morePanel: {
      activeUser: any;
      showExecutionMode: boolean;
      showPrivateOrder: boolean;
      showCommentPrivate: boolean;
      holdSTPisEnabled: boolean;
      autoForwardPricingEnabled: boolean;
      manualForwardPricingOnlyBMark: boolean;
      manualForwardPricingOnlyALGO: boolean;
      showClientCommentCheckbox: boolean;
      independentCommentSetting: boolean;
      fwdEnabled: boolean;
      stpAggIsEnabled: boolean;
    };
    financialsPanel: {
      markupLimitEnabled: boolean;
      markupAlgoEnabled: boolean;
      markupLimitCbx: boolean;
      markupAlgoCbx: boolean;
      markupRetrievalTypeAlgo: any;
      markupRetrievalTypeLimit: any;
      markupLimitDefault: any;
      markupAlgoDefault: any;
      markupRetrievalEnabled: boolean;
      showSuspend: boolean;
      traderGroupModifiable: boolean;
      traderGroupModifiableAlgo: boolean;
      traderGroupModifiableBmark: boolean;
      // NDF-specific settings
      customFixingAllowed: boolean;
      fixingDateModifiable: boolean;
      ndf1MOnly: boolean;
      // bmark
      markupBmarkDefault: boolean;
      markupRetrievalTypeBmark: any;
      markupBmarkCbx: boolean;
      markupModifyBmark: boolean;
      tgModifyBmark: boolean;
      // roles - user permissions
      markupAllowLimitEntry: boolean;
      markupAllowBmarkEntry: boolean;
      markupAllowAlgoEntry: boolean;
    };
    clientPanel: {
      clientTraderEnabled: boolean;
    };
  } = {
    morePanel: {
      activeUser: null,
      showExecutionMode: false,
      showPrivateOrder: false,
      showCommentPrivate: false,
      holdSTPisEnabled: false,
      autoForwardPricingEnabled: false,
      manualForwardPricingOnlyBMark: false,
      manualForwardPricingOnlyALGO: false,
      showClientCommentCheckbox: false,
      independentCommentSetting: false,
      fwdEnabled: false,
      stpAggIsEnabled: false,
    },
    financialsPanel: {
      markupLimitEnabled: false,
      markupAlgoEnabled: false,
      markupLimitCbx: false,
      markupAlgoCbx: false,
      markupRetrievalTypeAlgo: null,
      markupRetrievalTypeLimit: null,
      markupLimitDefault: null,
      markupAlgoDefault: null,
      markupRetrievalEnabled: false,
      showSuspend: true,
      traderGroupModifiable: true,
      traderGroupModifiableAlgo: true,
      traderGroupModifiableBmark: true,

      ndf1MOnly: false,
      customFixingAllowed: false,
      fixingDateModifiable: false,

      markupBmarkDefault: false,
      markupRetrievalTypeBmark: null,
      markupBmarkCbx: false,
      markupModifyBmark: false,
      tgModifyBmark: false,
      // roles
      markupAllowLimitEntry: false,
      markupAllowBmarkEntry: false,
      markupAllowAlgoEntry: false,
    },
    clientPanel: {
      clientTraderEnabled: false,
    },
  };

  private orderTypeConfig: {
    slAtRateEnabled: boolean;
    slBidEnabled: boolean;
    slAskEnabled: boolean;
    tSlAtRateEnabled: boolean;
    tSlBidEnabled: boolean;
    tSlAskEnabled: boolean;
    benchmarksEnabled: boolean;
    algosEnabled: boolean;
    marketOrdersEnabled: boolean;
    bmarkDesc: string;
    manualExecutionCallName: string;
    requestOrderTypeData: Map<RequestOrderType, PlRequestOrderTypeStrategy>;
    tradableAlgos: Set<string>;
  } = {
    slAtRateEnabled: false,
    slBidEnabled: false,
    slAskEnabled: false,
    tSlAtRateEnabled: false,
    tSlBidEnabled: false,
    tSlAskEnabled: false,
    benchmarksEnabled: false,
    algosEnabled: false,
    marketOrdersEnabled: false,
    bmarkDesc: "BMARK",
    manualExecutionCallName: "CALL",
    requestOrderTypeData: new Map<RequestOrderType, PlRequestOrderTypeStrategy>(),
    tradableAlgos: new Set(),
  };

  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    private settingsLookup: SystemConfigPromiseService,
    private activeUserLookup: ActiveUserProfilePromiseService,
    private userProfile: BfxUserProfileService,
    private profileLookup: ProfilePromiseActionService,
  ) {
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  private initialize(): void {
    this.settingsLookup.isPrimed().then(() => {
      Promise.all([
        this.initializeCurrentUser(),
        this.initializeSettings(),
        this.initializeMarkupSystemConfig(),
        this.initializeSystemConfigsForPanels(),
        this.initializeOrderTypeConfig(),
        this.initializeProfileSettings(),
      ]).then(() => {
        this.primedSubject.next();
      });
    });
  }

  private initializeCurrentUser(): Promise<void> {
    return this.userProfile.getUserData().then((value) => {
      this.currentUserId = value != null && value.userId != null ? value.userId.toString() : "";
    });
  }

  private initializeSettings(): Promise<void> {
    return Promise.all([
      this.settingsLookup.getSystemConfig(SysFeatures.independentEntryComments), // 0
      this.settingsLookup.getSystemConfig(SysFeatures.featuresEnablePrivateComments), // 1
      this.settingsLookup.getSystemConfig(SysFeatures.privatiseClientComments), // 2
      this.settingsLookup.getSystemConfig(SysFeatures.privatisePrivateComments), // 3
      this.settingsLookup.getSystemConfig("html.features.orderEntry.enableDebug"), // 4
      this.settingsLookup.getSystemConfig("html.features.orderEntry.enableStrictClientDataset"), // 5
      this.settingsLookup.getSystemConfig(SysFeatures.maximumGammaRungs), // 6
    ]).then(([...values]) => {
      this.independentComments = "Y" === values[0] || "y" === values[0];
      this.privateCommentsEnabled = "Y" === values[1] || "y" === values[1];
      this.privatizeClientComments = "Y" === values[2] || "y" === values[2];
      this.privatizePrivateComments = "Y" === values[3] || "y" === values[3];
      this.enableDebugBtn = "Y" === values[4] || "y" === values[4];
      this.strictClientDataset = "Y" === values[5] || "y" === values[5];
      this.maximumGammaRungs = values[6] != null ? parseInt(values[6], 10) : this.maximumGammaRungs;
    });
  }

  protected initializeMarkupSystemConfig(): Promise<void> {
    let promise = Promise.resolve();

    try {
      promise = Promise.all([
        this.activeUserLookup.getActiveUserData(),
        // financial panel configs
        this.settingsLookup.getSystemConfig(SysFeatures.markupEnabled), // [0]
        this.settingsLookup.getSystemConfig(SysFeatures.markupAlgoEntryEnabled), // [1]
        this.settingsLookup.getSystemConfig(SysFeatures.markupLimitCbx), // [2]
        this.settingsLookup.getSystemConfig(SysFeatures.markupAlgoCbx), // [3]
        this.settingsLookup.getSystemConfig(SysFeatures.markupRetrievalEnabled), // [4]
        // Markup Retrieval type
        this.settingsLookup.getSystemConfig(SysFeatures.markupRetrievalTypeLimit), // [5]
        this.settingsLookup.getSystemConfig(SysFeatures.markupRetrievalTypeAlgo), // [6]
        // Roles
        this.settingsLookup.hasRole(Roles.ORDER_ENTRY_MARKUP_LIMIT), // [7]
        this.settingsLookup.hasRole(Roles.ORDER_ENTRY_MARKUP_ALGO), // [8]

        this.settingsLookup.hasRole(Roles.ORDER_ENTRY_MARKUP_BENCHMARK), // [9]
        this.settingsLookup.getSystemConfig(SysFeatures.markupBmarkCbx), // [10]
        this.settingsLookup.getSystemConfig(SysFeatures.markupRetrievalTypeBmark), // [11]
        this.settingsLookup.hasRole(Roles.ROLE_ORDER_MODIFY_TRADER_GROUP_BENCHMARK), // [12]
        this.settingsLookup.hasRole(Roles.ROLE_ORDER_ENTRY_STP_AGGREGATION), // [13]
      ]).then(([activeUser, ...values]) => {
        //values[2] = 'N'; values[10] = 'N'; values[3] = 'N'; - for dev testing
        // Active User
        this.config.morePanel.activeUser = activeUser;

        this.config.financialsPanel.markupLimitEnabled = "Y" === values[0] || "y" === values[0];
        this.config.financialsPanel.markupAlgoEnabled = "Y" === values[1] || "y" === values[1];
        this.config.financialsPanel.markupLimitDefault =
          this.config.financialsPanel.markupLimitCbx = "Y" === values[2] || "y" === values[2];
        this.config.financialsPanel.markupAlgoDefault = this.config.financialsPanel.markupAlgoCbx =
          "Y" === values[3] || "y" === values[3];
        this.config.financialsPanel.markupRetrievalEnabled = "Y" === values[4] || "y" === values[4];
        this.config.financialsPanel.markupRetrievalTypeLimit = values[5];
        this.config.financialsPanel.markupRetrievalTypeAlgo = values[6];
        this.config.financialsPanel.markupAllowLimitEntry = true === values[7];
        this.config.financialsPanel.markupAllowAlgoEntry = true === values[8];
        // BMARK
        this.config.financialsPanel.markupAllowBmarkEntry =
          this.config.financialsPanel.markupModifyBmark = true === values[9];
        this.config.financialsPanel.markupBmarkCbx =
          this.config.financialsPanel.markupBmarkDefault =
            values[10] != null ? "Y" === values[10] || "y" === values[10] : false;
        //this.config.financialsPanel.markupBmarkCbx = this.config.financialsPanel.markupBmarkDefault;
        this.config.financialsPanel.markupRetrievalTypeBmark = values[11];
        this.config.financialsPanel.tgModifyBmark = values[12];
        this.config.morePanel.stpAggIsEnabled = values[13];
      });
    } catch (ex) {
      logger.out.error(
        `ERROR: oeSettings.initializeMarkupSystemConfig(): Initialization failed! ex: %O`,
        ex,
      );
    } finally {
      return promise;
    }
  }

  protected initializeSystemConfigsForPanels() {
    let promise = Promise.resolve();

    try {
      promise = Promise.all([
        this.activeUserLookup.getActiveUserData(),
        // more panel configs
        this.settingsLookup.getSystemConfig(SysFeatures.autoFillEnabled), // [0]
        this.settingsLookup.getSystemConfig(SysFeatures.privateOrdersEnabled), // [1]
        this.settingsLookup.getSystemConfig(SysFeatures.featuresEnablePrivateComments), // [2]
        this.settingsLookup.getSystemConfig(SysFeatures.enableHoldSTPCheckbox), // [3]
        this.settingsLookup.getSystemConfig(SysFeatures.restrictClientComments), // [4]
        this.settingsLookup.getSystemConfig(SysFeatures.independentEntryComments), // [5]
        this.settingsLookup.getSystemConfig(SysFeatures.fwdEnabled), // [6]
        //
        this.settingsLookup.getSystemConfig(SysFeatures.orderSuspensionEnabled), // [7]
        // client panel configs
        this.settingsLookup.getSystemConfig(SysFeatures.clientTraderUsersEnabled), // [8]
        // markup role check
        this.settingsLookup.hasRole(Roles.ROLE_ORDER_MODIFY_TRADER_GROUP_LIMIT), // [9]
        this.settingsLookup.hasRole(Roles.ORDER_REQUEST_NDF_ODD_FIXING), // [10]
        this.settingsLookup.hasRole(Roles.ORDER_REQUEST_NDF_CUSTOM_FIXING), // [11]
        this.settingsLookup.hasRole(Roles.ROLE_ORDER_MODIFY_TRADER_GROUP_BENCHMARK), // [12]
        this.settingsLookup.hasRole(Roles.ROLE_ORDER_MODIFY_TRADER_GROUP_ALGO), // [13]
        this.settingsLookup.hasRole(Roles.ROLE_ORDER_MODIFY_FWD_PRICING), // [14]
        this.settingsLookup.getSystemConfig(SysFeatures.fwdPricingModeBmark), // [15]
        this.settingsLookup.getSystemConfig(SysFeatures.fwdPricingModeALGO), // [16]
        this.settingsLookup.getSystemConfigBoolean(SysFeatures.ndf1MOnly), // [17]
      ]).then(([activeUser, ...values]) => {
        this.config.morePanel.activeUser = activeUser;
        // more panel configs
        this.config.morePanel.showExecutionMode = "Y" === values[0] || "y" === values[0]; // .toUpperCase() not used to avoid error on null.
        this.config.morePanel.showPrivateOrder = "Y" === values[1] || "y" === values[1];
        this.config.morePanel.showCommentPrivate = "Y" === values[2] || "y" === values[2];
        this.config.morePanel.holdSTPisEnabled = "Y" === values[3] || "y" === values[3];
        this.config.morePanel.showClientCommentCheckbox = "Y" === values[4] || "y" === values[4];
        this.config.morePanel.independentCommentSetting = "Y" === values[5] || "y" === values[5];
        this.config.morePanel.fwdEnabled = "Y" === values[6] || "y" === values[6];
        //
        this.config.financialsPanel.showSuspend = "Y" === values[7] || "y" === values[7];
        // client panel configs
        this.config.clientPanel.clientTraderEnabled = "Y" === values[8] || "y" === values[8];
        this.config.financialsPanel.traderGroupModifiable = true === values[9];
        this.config.financialsPanel.fixingDateModifiable = true === values[10];
        this.config.financialsPanel.customFixingAllowed = true === values[11];
        this.config.financialsPanel.traderGroupModifiableBmark = true === values[12];
        this.config.financialsPanel.traderGroupModifiableAlgo = true === values[13];
        this.config.morePanel.autoForwardPricingEnabled = true === values[14];
        this.config.morePanel.manualForwardPricingOnlyBMark = "MANUAL" === values[15];
        this.config.morePanel.manualForwardPricingOnlyALGO = "MANUAL" === values[16];
        this.config.financialsPanel.ndf1MOnly = values[17];
      });
    } catch (ex) {
      logger.out.error(
        `ERROR: oeSettings.initializeMorePanelConfigs(): Initialization failed! ex: %O`,
        ex,
      );
    } finally {
      return promise;
    }
  }

  private initializeOrderTypeConfig(): Promise<void> {
    return Promise.all([
      this.settingsLookup.getSystemConfig(SysFeatures.slAtRateEnabled), //0
      this.settingsLookup.getSystemConfig(SysFeatures.slBidEnabled), //1
      this.settingsLookup.getSystemConfig(SysFeatures.slAskEnabled), //2
      this.settingsLookup.getSystemConfig(SysFeatures.tSlAtRateEnabled), //3
      this.settingsLookup.getSystemConfig(SysFeatures.tSlBidEnabled), //4
      this.settingsLookup.getSystemConfig(SysFeatures.tSlAskEnabled), //5
      this.settingsLookup.getSystemConfig(SysFeatures.benchmarksEnabled), //6
      this.settingsLookup.getSystemConfig(SysFeatures.algosEnabled), //7
      this.settingsLookup.getSystemConfig(SysFeatures.marketOrdersEnabled), //8
      this.settingsLookup.getSystemConfig(SysFeatures.fixingOrderShortName), // 9
      this.settingsLookup.getSystemConfig(SysFeatures.manualExecutionCallName), // 10
      this.activeUserLookup.getActiveUserData(), // 11
    ]).then(([...values]) => {
      this.orderTypeConfig.slAtRateEnabled = values[0] === "Y" || values[0] === "y"; // .toUpperCase() not used to avoid error on null.
      this.orderTypeConfig.slBidEnabled = values[1] === "Y" || values[0] === "y";
      this.orderTypeConfig.slAskEnabled = values[2] === "Y" || values[0] === "y";
      this.orderTypeConfig.tSlAtRateEnabled = values[3] === "Y" || values[0] === "y";
      this.orderTypeConfig.tSlBidEnabled = values[4] === "Y" || values[0] === "y";
      this.orderTypeConfig.tSlAskEnabled = values[5] === "Y" || values[0] === "y";
      this.orderTypeConfig.benchmarksEnabled = values[6] === "Y" || values[0] === "y";
      this.orderTypeConfig.algosEnabled = values[7] === "Y" || values[0] === "y";
      this.orderTypeConfig.marketOrdersEnabled = values[8] === "Y" || values[0] === "y";
      this.orderTypeConfig.bmarkDesc = values[9];
      this.orderTypeConfig.manualExecutionCallName =
        typeof values[10] == "string" && values[10].length > 0 ? values[10] : "CALL";
      this.orderTypeConfig.requestOrderTypeData = this.processRequestOrderTypes(
        values[11].requestOrderTypes,
      );
      this.orderTypeConfig.tradableAlgos = values[11].tradableAlgos;

      this.typesPromise.resolve();
    });
  }

  processRequestOrderTypes(
    requestOrderTypes: Set<PlRequestOrderTypeStrategy>,
  ): Map<RequestOrderType, PlRequestOrderTypeStrategy> {
    let ret: Map<RequestOrderType, PlRequestOrderTypeStrategy> = new Map<
      RequestOrderType,
      PlRequestOrderTypeStrategy
    >();
    if (null != requestOrderTypes) {
      requestOrderTypes.forEach((type) => {
        ret.set(type.id, type);
      });
    }
    return ret;
  }

  public getPlRequestOrderTypeStrategy(rot: RequestOrderType): PlRequestOrderTypeStrategy {
    let ret: PlRequestOrderTypeStrategy = this.orderTypeConfig.requestOrderTypeData.get(rot);
    return ret;
  }

  public getBmarkDesc(): string {
    return this.orderTypeConfig.bmarkDesc;
  }

  public getManualExecutionCallName(): string {
    return this.orderTypeConfig.manualExecutionCallName;
  }

  private initializeProfileSettings(): Promise<void> {
    return this.profileLookup
      .getProfileConfig(ProfileSettingKeys.userOrderEntryPreferencesSettings)
      .then((profileValue) => {
        this.assignOrderEntryProfileValues(profileValue);
        this.profileUpdatesSub = this.profileLookup
          .getProfileConfigUpdates(ProfileSettingKeys.userOrderEntryPreferencesSettings)
          .subscribe(this.assignOrderEntryProfileValues.bind(this));
      });
  }

  private assignOrderEntryProfileValues(profileValue: any) {
    let defaultProfileValue = {
      multiplyAmountToggle: "N",
      multiplyAmountModel: 0,
      newOrderTypeDefault: "spot",
    };
    let expandedMoreObj = null;

    try {
      expandedMoreObj = profileValue ? JSON.parse(profileValue) : defaultProfileValue;
    } catch (e) {
      logger.out.error("Error reading profile settings key userOrderEntryPreferencesSettings: ", e);
    }

    if (
      expandedMoreObj &&
      expandedMoreObj != null &&
      expandedMoreObj.multiplyAmountModel != null &&
      expandedMoreObj.multiplyAmountModel > 0
    ) {
      this.multiplyAmountUserValue = expandedMoreObj.multiplyAmountModel;
    }

    if (
      expandedMoreObj &&
      expandedMoreObj != null &&
      expandedMoreObj.multiplyAmountToggle === "Y"
    ) {
      this.multiplyAmountUserToggle = true;
    } else {
      this.multiplyAmountUserToggle = false;
    }
    if (expandedMoreObj && expandedMoreObj != null && expandedMoreObj.newOrderTypeDefault != null) {
      this.newOrderTypeDefault = expandedMoreObj.newOrderTypeDefault;
    } else {
      this.newOrderTypeDefault = defaultProfileValue.newOrderTypeDefault;
    }
  }

  /**********************************************************************/
  /* GETTERS */

  public getSysConfigForMorePanel(): any {
    return this.config.morePanel;
  }

  public getSysConfigForFinancialsPanel(): any {
    return this.config.financialsPanel;
  }

  public getSysConfigForClientPanel(): any {
    return this.config.clientPanel;
  }

  public getSysConfigForOrderTypes(): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        this.typesPromise.get().then(() => {
          resolve(this.orderTypeConfig);
        });
      } catch (err) {
        reject(err);
      }
    });
  }

  public areMarketOrdersEnabled(): boolean {
    return this.orderTypeConfig.marketOrdersEnabled;
  }

  /**
   * Return a new set with all currently defined tradable algo Ids
   * @returns
   */
  public getTradableAlgos(): Set<string> {
    return new Set(this.orderTypeConfig.tradableAlgos);
  }
  /**********************************************************************/
  /* Golden Layout  */

  public getWindowPopoutConfiguration(): GLPopoutConfiguration {
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = false;
    config.preferredSize = { width: "auto", height: "auto" };
    config.minimumSize = { width: 530, height: 500 };
    config.ignoreScreenEvent = true;
    return config;
  }

  /**********************************************************************/
  /* DEBUG */
  // GENERIC
  public setClientDatasetMode(value: boolean): void {
    if (typeof value === "boolean") this.strictClientDataset = value;
  }

  isSTPAggAvailable(
    orderType: CubiqTS.RequestOrderType,
    partFillEnabled: boolean | CubiqTS.FillSupport,
  ): boolean {
    let ret: boolean = false;
    let rot: CubiqTS.PlRequestOrderTypeStrategy = this.getPlRequestOrderTypeStrategy(orderType);

    partFillEnabled =
      typeof partFillEnabled === "boolean"
        ? partFillEnabled
        : CubiqTS.FillSupport.ALLOW_PARTIAL === partFillEnabled;

    if (null != rot) {
      ret =
        rot?.isSTPAggregatedFg &&
        (orderType != CubiqTS.RequestOrderType.TP || partFillEnabled === true);
    }
    return ret;
  }
}
